/**
 * Background Jobs — Settings. GET /heartbeat.
 *
 * How the engine is configured, and whether anything is actually driving it. It
 * answers the one question the dashboard tiles cannot: *is anything going to run?*
 * A schedule can be enabled, due, and still never fire because nothing is driving
 * it — the counts look healthy while nothing executes.
 *
 * Mostly a READ-ONLY view of what the engine is actually doing. The tunables a
 * project may override — engine state, max concurrency, lock TTL, run retention,
 * poll interval and the tick token — are editable here through an "Edit" dialog,
 * but that dialog does NOT keep its own copy: it reads and writes AppConfiguration
 * (`BackgroundJobs.*`), one typed audited row per tunable — the same store the App
 * Configuration screen edits. One source of truth, so a save here shows up there
 * and vice-versa. Everything else on this screen (the live poller, the deployment
 * ceilings/floors, the heartbeat) stays read-only.
 *
 * The Edit button is gated on APP_CONFIG.UPDATE — the very permission the backend
 * requires for that write — so the button and the server never disagree about
 * whether a save will be accepted.
 *
 * What this screen adds over the App Configuration list is the half a settings
 * form cannot show: the live poller, whether it is running, and when the next job
 * is due.
 *
 * ONE project — the one this session signed into. There is no chooser: the tab
 * names the project it is describing and shows only that. When it happens to be
 * the ADMIN project (asked of the server via `adminAppId`, derived there from the
 * INTERNAL partner — never guessed from an appCode), the numbers stop being
 * overrides and become the ceiling, floor and default that every other project
 * inherits live, so the screen says so and drops the "overridden" badge.
 *
 * Four things worth knowing about what is shown:
 *
 *  · `pollerRunning` is the LIVE task's state, not the config flag. False while
 *    `engineEnabled` is true is the interesting case: the engine was switched on
 *    and its poller has since died, which configuration alone cannot reveal.
 *
 *  · The reading is PER PROCESS. With several API instances behind a balancer,
 *    this describes whichever one answered. There is no registry of instances to
 *    aggregate, so claiming a deployment-wide status would be a guess.
 *
 *  · No token is ever displayed. The endpoint returns only whether a global tick
 *    token exists; the value drives every project's jobs and does not belong in
 *    a browser. Per-project tokens are set in App Configuration, write-only.
 *
 * There is deliberately no "run a cycle now" button: /tick authenticates with a
 * service token, not the user's session, so a browser cannot call it without
 * embedding that secret. To force one job, use Run now on the Jobs tab — that
 * path is RBAC-gated and audited.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from 'sharedUi/Button';
import { Dialog } from 'sharedUi/Dialog';
import { Message } from 'sharedUi/Message';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { backgroundJobService } from '../../../services/background-job.service';
import { projectsService } from '../../../services/projects.service';
import { getBackgroundJobSettings, saveBackgroundJobSettings } from '../../../services/app-config.service';
import { useToastApi } from '../../../hooks/use-toast-api';
import FormInput from '../../../components/forms/form-input';
import FormSelect from '../../../components/forms/form-select';
import { useSelector } from 'react-redux';
import { PERMISSIONS } from '../../../constants/permissions';
import DiscardChangesDialog from '../../../components/dialogs/discard-changes-dialog';

/**
 * The numeric keys a project may override, kept in step with OVERRIDABLE in
 * `_4s_sdlc/jobs/settings_resolver.py`. Only used to decide which rows can show an
 * "overridden" badge — a key missing here simply never claims to be overridden,
 * which is the safe direction to be wrong in.
 */
type EditableKey =
  | 'maxConcurrency'
  | 'lockTtlSeconds'
  | 'runRetentionDays'
  | 'pollIntervalSeconds';

/** The switch is tri-state: absent means "follow the deployment", which is not the
 *  same as an explicit true. Only `false` disables — see is_engine_enabled(). */
const ENGINE_KEY = 'engineEnabled';

/** "in 4m", "in 2h 10m", "due now", or a plain timestamp when further out. */
function untilNextRun(iso?: string | null): string {
  if (!iso) return 'nothing scheduled';
  const ms = Date.parse(iso) - Date.now();
  if (Number.isNaN(ms)) return 'nothing scheduled';
  if (ms <= 0) return 'due now';

  // Seconds are shown under the hour, and the minutes are FLOORED rather than
  // rounded. Rounding produced "in 5m" for something 4m 32s away — a countdown
  // that reads high, then appears to stall while it catches up with itself.
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `in ${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `in ${mins}m ${secs % 60}s`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `in ${hours}h ${mins % 60}m`;
  return formatTimestamp(iso);
}

/**
 * One setting.
 *
 * `overridden` is the only mark a row carries. Nothing here is editable in place —
 * this screen come from the environment and cannot be changed from a browser at
 * all, so nothing here is edited in place.
 *
 * `overridden` says this project has set its own value rather than inheriting the
 * deployment default — the fact an operator debugging why one project behaves
 * differently from another is actually looking for. Suppressed on the admin
 * project, whose stored values ARE the default and so override nothing.
 */
const Row = ({ label, value, hint, overridden }: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  overridden?: boolean;
}) => (
  <div className="flex items-start justify-between gap-4 border-b border-gray-100 py-2 last:border-0 dark:border-gray-800">
    <div className="min-w-0">
      <p className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
        {/* Wrapped rather than left bare. A TEXT node sitting next to a conditional
            element is the shape that makes React insert into a position it no
            longer owns when the condition flips — which it does here the moment
            the settings query resolves, on a row rendered a dozen times. */}
        <span>{label}</span>
        {overridden && (
          <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
            overridden
          </span>
        )}
      </p>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
    <div className="flex shrink-0 items-center gap-2 text-sm font-medium">
      <span>{value}</span>
    </div>
  </div>
);

const Pill = ({ ok, children }: { ok: boolean; children: React.ReactNode }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
      ok
        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    }`}
  >
    {children}
  </span>
);

type EngineState = 'inherit' | 'enabled' | 'disabled';

/** A blank field means "inherit"; anything else is a value, INCLUDING 0.
 *
 * Not `v ? Number(v) : null`: that maps "0" to null, and 0 is meaningful for
 * runRetentionDays (keep run history forever). Kept identical to the same helper in
 * project-settings-tab so the two editors of these fields cannot disagree. */
const numOrNull = (v: string): number | null => {
  const t = (v ?? '').trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : null;
};

/**
 * Edits ONLY this project's background-job tunables, in place.
 *
 * The values live in Application Configuration — one typed, audited row per
 * setting under `BackgroundJobs.*` — and this writes them there, row by row, the
 * same store the App Configuration screen edits. One source of truth: a save here
 * shows up there and vice-versa.
 *
 * A blank field is written as an explicit null, which is what returns the setting
 * to the deployment default. Omitting it instead would keep the last value, so a
 * field an operator cleared would quietly stay in force.
 *
 * The blank-means-inherit rule and the write-only tick-token contract are the
 * component's own; they are documented here rather than mirrored, because the
 * two forms behave the same over the same fields.
 */
const EditBackgroundJobsDialog = ({
  open, onClose, projectId, projectName, config, defaults, hasTickToken, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  /** The project's stored background-job overrides, from AppConfiguration. */
  config: Record<string, unknown>;
  /** Deployment defaults, shown as the placeholder each field inherits when blank. */
  defaults: {
    maxConcurrency?: number;
    lockTtlSeconds?: number;
    runRetentionDays?: number;
    pollIntervalSeconds?: number;
  };
  hasTickToken: boolean;
  /** Awaited before the dialog closes, so the screen behind repaints from the server. */
  onSaved: () => Promise<void> | void;
}) => {
  const { toast, ok, fail } = useToastApi();
  const num = (v: unknown) => (typeof v === 'number' ? String(v) : '');

  const [engine, setEngine] = useState<EngineState>(
    config.engineEnabled === true ? 'enabled'
      : config.engineEnabled === false ? 'disabled' : 'inherit');
  const [concurrency, setConcurrency] = useState(num(config.maxConcurrency));
  const [lockTtl, setLockTtl] = useState(num(config.lockTtlSeconds));
  const [retention, setRetention] = useState(num(config.runRetentionDays));
  const [pollInterval, setPollInterval] = useState(num(config.pollIntervalSeconds));
  // Write-only, exactly like the Mongo URI: the server returns only whether one is
  // stored. Blank means "keep the current token", never "clear it".
  const [tickToken, setTickToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  // Snapshot of the values this dialog opened with — what the dirty check compares
  // against. The dialog is mounted fresh per open, so a ref taken at first render
  // is exactly the opening state.
  const initialRef = useRef<string>(JSON.stringify({
    engine: config.engineEnabled === true ? 'enabled'
      : config.engineEnabled === false ? 'disabled' : 'inherit',
    concurrency: num(config.maxConcurrency),
    lockTtl: num(config.lockTtlSeconds),
    retention: num(config.runRetentionDays),
    pollInterval: num(config.pollIntervalSeconds),
    tickToken: '',
  }));

  /* The bounds BackgroundJobsSettingsSchema enforces on the backend, checked here
     first so an out-of-range value is caught on the field rather than returned as a
     whole-payload 422. Blank is always valid — it means inherit. */
  const rangeError = (): string | null => {
    const bounds: Array<[string, string, number, number]> = [
      ['Max concurrency', concurrency, 1, 100],
      ['Lock TTL (seconds)', lockTtl, 1, 3600],
      ['Run retention (days)', retention, 0, 365],
      ['Poll interval (seconds)', pollInterval, 1, 3600],
    ];
    for (const [label, raw, lo, hi] of bounds) {
      const t = (raw ?? '').trim();
      if (!t) continue;
      const n = Number(t);
      if (!Number.isInteger(n)) return `${label} must be a whole number, or blank to inherit.`;
      if (n < lo || n > hi) return `${label} must be between ${lo} and ${hi}, or blank to inherit.`;
    }
    return null;
  };

  const save = async () => {
    const re = rangeError();
    if (re) { setError(re); return; }
    setError(null);
    try {
      setSaving(true);
      // One configuration row per tunable, not a settings block. These moved to
      // Application Configuration, and the project-settings endpoint now refuses
      // the block outright — deliberately, since writing it there would have
      // reported success while changing nothing anything reads.
      //
      // A blank field is sent as an explicit null, which is what returns the
      // setting to the deployment default. The tick token is omitted when blank:
      // sending null there would encrypt an empty credential and revoke a working
      // token every time an operator saved an unrelated field.
      await saveBackgroundJobSettings(projectId, {
        engineEnabled: engine === 'inherit' ? null : engine === 'enabled',
        maxConcurrency: numOrNull(concurrency),
        lockTtlSeconds: numOrNull(lockTtl),
        runRetentionDays: numOrNull(retention),
        pollIntervalSeconds: numOrNull(pollInterval),
        ...(tickToken.trim() ? { rawTickToken: tickToken.trim() } : {}),
      });
      await onSaved();
      ok(tickToken.trim()
        ? 'Background job settings saved and the tick token rotated. The old token no longer works.'
        : 'Background job settings saved.');
      onClose();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const ph = (v?: number) => (v !== undefined ? `Inherit (e.g. ${v})` : 'Inherit');

  /* Same promise the form PAGES make (job-form, schedule-form): closing with edits
     in flight asks first. This one stays a dialog rather than becoming a route —
     it edits the CURRENT project's settings in place and has no record identity of
     its own, and a page would have to re-derive the project, the deployment
     defaults and the token state that only exist on this tab. The guard is what
     matters, so it is applied here directly. */
  const isDirty = JSON.stringify({ engine, concurrency, lockTtl, retention, pollInterval, tickToken })
    !== initialRef.current;

  const requestClose = () => {
    if (isDirty && !saving) { setConfirmLeave(true); return; }
    onClose();
  };

  return (
    <Dialog
      header="Edit background job settings"
      visible={open}
      style={{ width: '34rem' }}
      onHide={requestClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button label="Cancel" text onClick={requestClose} disabled={saving} />
          <Button label="Save" icon="pi pi-check" onClick={save} loading={saving} />
        </div>
      }
    >
      {toast}
      {/* Named again here, at the last moment before a write — the one place an
          operator who is on the wrong project can still notice. */}
      <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs dark:border-amber-800 dark:bg-amber-900/20">
        Editing <strong>{projectName}</strong> · saved to Application Configuration (<code>BackgroundJobs.*</code>).
      </div>
      <p className="mb-3 text-xs text-ink-muted">
        These override the deployment default for this project alone. Leave a field blank
        to inherit. A typed <code>0</code> is a value — for run retention it means keep
        history forever.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormSelect
          name="engine"
          label="Engine status"
          value={engine}
          onChange={(e) => setEngine(e.target.value as EngineState)}
          options={[
            { value: 'inherit', label: 'Inherit deployment default' },
            { value: 'enabled', label: 'Force enabled (active)' },
            { value: 'disabled', label: 'Force disabled (paused)' },
          ]}
        />
        <FormInput
          name="concurrency"
          label="Max concurrency"
          type="number"
          value={concurrency}
          onChange={(e) => setConcurrency(e.target.value)}
          placeholder={ph(defaults.maxConcurrency)}
        />
        <FormInput
          name="lockTtl"
          label="Lock TTL (seconds)"
          type="number"
          value={lockTtl}
          onChange={(e) => setLockTtl(e.target.value)}
          placeholder={ph(defaults.lockTtlSeconds)}
        />
        <FormInput
          name="retention"
          label="Run retention (days)"
          type="number"
          value={retention}
          onChange={(e) => setRetention(e.target.value)}
          placeholder={ph(defaults.runRetentionDays)}
        />
        <FormInput
          name="pollInterval"
          label="Poll interval (seconds)"
          type="number"
          value={pollInterval}
          onChange={(e) => setPollInterval(e.target.value)}
          placeholder={ph(defaults.pollIntervalSeconds)}
        />
      </div>

      {/* The per-project heartbeat credential. Write-only, exactly like Project
          Settings: typing replaces it, blank keeps the current one. */}
      <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
        <label className="mb-1 block text-sm font-medium" htmlFor="bg-tick-token">
          Tick token (external heartbeat)
          <span className="ml-2 text-xs font-normal text-gray-400">
            {hasTickToken ? 'configured' : 'not set'}
          </span>
        </label>
        <input
          id="bg-tick-token"
          type="password"
          autoComplete="new-password"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          placeholder={hasTickToken
            ? 'Leave blank to keep the current token'
            : 'A long random string — this project only'}
          value={tickToken}
          onChange={(e) => setTickToken(e.target.value)}
        />
        <p className="mt-1 text-xs text-gray-400">
          Lets an external scheduler drive <strong>only this project’s</strong> jobs. The
          stored value is never shown — typing here replaces it, and the previous token
          stops working immediately.
        </p>
      </div>

      {error && <p className="mt-3 text-xs text-red-600">{error}</p>}

      <DiscardChangesDialog
        visible={confirmLeave}
        onStay={() => setConfirmLeave(false)}
        onLeave={() => { setConfirmLeave(false); onClose(); }}
        testId="BackgroundJobSettings.Dialog.Leave"
      />
    </Dialog>
  );
};

const SettingsTab = () => {
  const queryClient = useQueryClient();
  // Opens the in-place editor for this project's background-job overrides. It writes
  // to the same Application Configuration rows, so there is one source of truth.
  const [editing, setEditing] = useState(false);

  /* A one-second heartbeat for the live readings on this screen.
   *
   * "Next due" and "Last beat" are computed during render from a timestamp, so
   * without something forcing a re-render they only change when the query
   * refetches — the countdown froze, then jumped. Worse, it would sit on "due now"
   * long after the job had run, because the value was computed once and never
   * recomputed. The data was never wrong; the screen stopped doing the arithmetic. */
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const q = useQuery({
    queryKey: ['bg-heartbeat'],
    queryFn: () => backgroundJobService.getHeartbeat(),
    // 30s was too slow for a panel showing a countdown and a liveness reading: a
    // job on a short interval could fire and finish entirely between refetches,
    // leaving a fire time on screen that had already passed.
    staleTime: 5_000,
    refetchInterval: 10_000,
  });

  /* This tab is gated on BACKGROUND_JOB.READ, but the overrides it edits are
     APPLICATION CONFIGURATION — a different resource with its own grants. Someone
     can hold every background-job permission and none of these, so the editor is
     gated separately and explains itself rather than 403-ing on save. Reading the
     permission store directly because these keys belong to another module;
     useJobPermissions covers the background-job ones only. */
  const authKeys = useSelector(
    (s: { auth?: { permissions?: string[] } }) => s.auth?.permissions || []);
  const perms = useMemo(() => new Set(authKeys), [authKeys]);
  // Editing lives in Application Configuration now, gated on the permission that
  // actually governs the write — not on APP.UPDATE, which only renames a project.
  // APP_CONFIG.UPDATE, not APP_INTEGRATION.MANAGE: this dialog writes
  // configuration rows now, not the project-settings document, so the gate has to
  // be the one that actually authorises the write it performs. Leaving the old
  // key would show an Edit button to someone the API then refuses.
  const canEditAppConfig = perms.has(PERMISSIONS.APP_CONFIG_UPDATE);
  // PARTNER_CONNECTION_UPDATE is no longer consulted here: this tab is read-only
  // now that the values live in Application Configuration, gated on its own
  // permissions. Reading still needs the connection permissions below only for as
  // long as the legacy fallback exists.

  /* The project this session logged into, stored at login — the same source
     org-structure uses. Only a default here, never an authority: the server
     decides what this caller may actually reach. */
  const ownAppId = localStorage.getItem('appId') || null;

  /* A NAME for that id, and nothing else. There is no project chooser here any
     more: this screen describes the project the session is signed into, and a
     selector that silently repointed the rows was how the wrong project's numbers
     got read as this one's.

     Deliberately /accessible rather than /apps/get/{id}. The latter needs APP.READ
     — which a background-job operator need not hold — and writes an APP.VIEW audit
     entry on every render of a tab that merely wants to print "Core Admin". This
     one needs only a valid token and returns id, name and code. */
  const projectsQ = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: () => projectsService.getAccessible(),
    staleTime: 300_000,
  });
  const selectedAppId = ownAppId;
  const projectName = useMemo(() => {
    const hit = (projectsQ.data ?? []).find((p) => p._id === selectedAppId);
    // The raw id is a poor label but an honest one; "this project" would read as
    // resolved when the lookup simply has not answered yet.
    return hit?.name || selectedAppId || 'this project';
  }, [projectsQ.data, selectedAppId]);

  /* Overrides come from AppConfiguration, which is where they now live — one
     typed, audited row per tunable. Read from there rather than from the project
     settings endpoint, which no longer carries the block at all: reading two
     stores is what let this screen disagree with the one that edits them. */
  const settingsQ = useQuery({
    queryKey: ['app-config-background-jobs', selectedAppId],
    queryFn: () => getBackgroundJobSettings(selectedAppId as string),
    enabled: !!selectedAppId,
    staleTime: 30_000,
  });

  /* One reason, shared by the top button and every per-field pencil, so they can
     never disagree about whether editing is possible.

     The order matters. "No connection exists" is what a missing PARTNER_CONNECTION
     read grant LOOKS like from here — the list comes back empty either way — so
     the permission cases are tested first. Reporting a permission problem as a
     missing connection would send someone to create one they already have. */
  /* The Edit button is shown only when `canEditAppConfig && selectedAppId`
     (see its render site). The permission checked there is exactly the one the
     backend enforces on the `backgroundJobs` block of a settings write
     (APP_INTEGRATION.MANAGE), so a visible button always corresponds to a save the
     server will accept — and someone without the grant sees no button at all. */

  /* Whether a value is this project's own rather than the deployment default.
     Read from the project's settings, not from the heartbeat:
     the heartbeat reports the EFFECTIVE value and cannot say where it came from,
     so an override that happens to equal the default is indistinguishable there. */
  const overrides = (settingsQ.data || {}) as Record<string, unknown>;
  // `isAdminProject` is not in scope this early — the heartbeat has not been
  // narrowed yet — so the suppression is applied at each call site below via
  // `badge()`, which is defined once the flag exists.
  const isOverridden = (key: EditableKey) => typeof overrides[key] === 'number';

  /* What this project actually runs on.
   *
   * The rows used to read the heartbeat directly, which reports the DEPLOYMENT
   * defaults — so a project with its own overrides saw the admin's numbers here
   * while the engine ran on different ones. The screen contradicted the engine.
   *
   * Layered the same way the engine does: this project's stored value if it has
   * one, otherwise the deployment default. On the admin project the two are the
   * same value by construction, which is why no badge is drawn there. */
  const effective = (key: EditableKey, deploymentValue: number | undefined) =>
    (typeof overrides[key] === 'number' ? (overrides[key] as number) : deploymentValue);

  if (q.isError) {
    return (
      <Message
        className="w-full justify-start"
        severity="error"
        text="The heartbeat could not be read. The other tabs are unaffected."
      />
    );
  }

  const d = q.data;
  if (!d) return <div className="py-10 text-sm text-gray-500">Loading…</div>;

  // The case that matters: switched on, but nothing is driving it in this
  // process. Called out explicitly because every other figure still looks fine.
  const stopped = d.engineEnabled && !d.pollerRunning;

  /* Whether this session's project is the one supplying the deployment defaults.
     Compared against the id the server derived from the INTERNAL partner, not
     against an appCode — 'CORE_ADMIN' is a convention, and a deployment that
     renamed it would silently show every project the wrong banner.

     It changes what the numbers below MEAN. For the admin project they are not
     overrides at all: they are the ceiling, floor and default that every project
     setting nothing inherits live. So the "overridden" badge is suppressed — a
     value that IS the default cannot be an override of it — and the card says so
     outright, because editing them here changes every other project at once. */
  const isAdminProject = !!d.adminAppId && !!selectedAppId && d.adminAppId === selectedAppId;
  const badge = (key: EditableKey) => !isAdminProject && isOverridden(key);

  // Alive but not beating. A loop wedged on a slow query keeps `pollerRunning`
  // true forever, so the task's existence is not evidence of progress — the gap
  // since the last completed cycle is. Two intervals of grace absorbs a cycle
  // that simply took longer than usual.
  const since = d.secondsSinceLastCycle;
  const wedged =
    d.pollerRunning && since !== null && since > Math.max(30, d.pollIntervalSeconds * 3);

  // Kept although the "Last beat" row is gone: this still phrases the gap in the
  // stuck-poller warning below, which is the case where the number actually
  // matters — a poller alive but not turning looks healthy by every other reading.
  const beat =
    since === null
      ? 'no cycle yet'
      : since <= 1
        ? 'just now'
        : since < 60
          ? `${since}s ago`
          : `${Math.round(since / 60)}m ago`;

  return (
    <div className="space-y-4">
      {/* Says plainly that this screen only reads, and where the writing happens.
          A dozen read-only rows with no explanation invites the assumption that
          the values simply cannot be changed at all. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Always names the project. Numbers on a screen that does not say whose
            they are is how one project's configuration gets read as another's. */}
        <span
          className="flex flex-wrap items-center gap-1.5 text-xs text-gray-400"
          data-testid="job-settings-project"
        >
          <span>Showing</span>
          <strong className="text-gray-600 dark:text-gray-300">{projectName}</strong>
          {isAdminProject ? (
            <span>— the deployment defaults, inherited by every other project.</span>
          ) : (
            <>
              <span>· rows marked</span>
              <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                overridden
              </span>
              <span>are set for it in App Configuration; the rest are inherited.</span>
            </>
          )}
        </span>
        <div className="flex items-center gap-2">
          {/* Opens the in-place editor for THIS project's background-job overrides.
              It writes Application Configuration rows, the same store the App
              Configuration screen edits, so a save here appears there and vice-versa.
              Only the tunables are editable; the rest of this screen stays a
              read-only view of what the engine is actually doing.

              Rendered ONLY when the caller can actually edit — gated on
              APP_CONFIG.UPDATE, the permission the backend enforces on that write,
              and on there being a project to edit. Without that grant the button is
              not shown at all rather than shown disabled: an action a user can never
              take is noise, not information. */}
          {canEditAppConfig && selectedAppId && (
            <Button
              label="Edit"
              icon="pi pi-pencil"
              outlined
              size="small"
              data-testid="edit-job-settings"
              onClick={() => setEditing(true)}
            />
          )}
        </div>
      </div>

      {/* Mounted only while open, so the form is rebuilt from the CURRENT settings
          each time — a second open shows freshly stored values, not the ones from
          before the last save. */}
      {editing && selectedAppId && (
        <EditBackgroundJobsDialog
          open
          onClose={() => setEditing(false)}
          projectId={selectedAppId}
          projectName={projectName}
          config={overrides}
          defaults={{
            maxConcurrency: d.maxConcurrency,
            lockTtlSeconds: d.lockTtlSeconds,
            runRetentionDays: d.runRetentionDays,
            pollIntervalSeconds: d.pollIntervalSeconds,
          }}
          hasTickToken={!!settingsQ.data?.hasTickToken}
          onSaved={async () => {
            // refetch, not invalidate: the read-only rows behind the dialog must show
            // the new values the instant it closes. Both matter — the configuration
            // this wrote to, and the heartbeat that reports the effective (layered)
            // values the engine will actually run on.
            //
            // The key must match the query above. It pointed at 'project-settings'
            // after the store moved, so a save refetched something this screen no
            // longer reads and the rows behind the dialog stayed stale — the exact
            // failure this refetch exists to prevent.
            await Promise.all([
              queryClient.refetchQueries({
                queryKey: ['app-config-background-jobs', selectedAppId] }),
              queryClient.refetchQueries({ queryKey: ['bg-heartbeat'] }),
            ]);
          }}
        />
      )}

      {/* Keyed, and deliberately so. These are three siblings of the SAME element
          type that appear and disappear independently, and `stopped` / `wedged` are
          derived from the clock — the 1s ticker flips them mid-life. React reconciles
          unkeyed same-type siblings BY POSITION, so when one vanishes it matches the
          wrong element to the wrong slot and then inserts relative to a node that is
          no longer there: "insertBefore ... not a child of this node". A stable key
          per message makes the match by identity instead. */}
      {stopped && (
        <Message
          key="poller-stopped"
          className="w-full justify-start"
          severity="warn"
          text="The engine is enabled but this instance's poller is not running. Due jobs will not fire here unless an external heartbeat is driving them."
        />
      )}
      {wedged && (
        <Message
          key="poller-wedged"
          className="w-full justify-start"
          severity="error"
          text={`The poller is alive but has not completed a cycle for ${beat}, well past the ${d.pollIntervalSeconds}s interval. It is likely stuck rather than idle.`}
        />
      )}
      {isAdminProject && (
        <Message
          key="admin-project"
          className="w-full justify-start"
          severity="info"
          text={`${projectName} supplies the deployment defaults. These are the ceiling for max concurrency, the floor for poll interval, and the fallback for everything else — every project that sets nothing of its own follows them live, so changing one here changes those projects too.`}
        />
      )}
      {!d.engineEnabled && (
        <Message
          key="engine-off"
          className="w-full justify-start"
          severity="info"
          text="The background job engine is switched off (JOB_ENGINE_ENABLED). Schedules are stored but nothing runs."
        />
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <h3 className="mb-2 text-sm font-semibold">
          {isAdminProject ? 'In-process poller — deployment defaults' : 'In-process poller'}
        </h3>
        <Row
          label="Engine enabled"
          hint="JOB_ENGINE_ENABLED — whether the poller was meant to start"
          value={<Pill ok={d.engineEnabled}>{d.engineEnabled ? 'yes' : 'no'}</Pill>}
        />
        <Row
          label="Poller running"
          hint="the live task on THIS instance, not the setting"
          value={<Pill ok={d.pollerRunning}>{d.pollerRunning ? 'running' : 'stopped'}</Pill>}
        />
        <Row
          label="Poll interval"
          hint="must be ≤ the smallest configured interval, or sub-minute jobs fire late"
          value={`${effective('pollIntervalSeconds', d.pollIntervalSeconds)}s`}
        />
        {/* First row, because it governs every row under it: if this project's
            engine is off, none of the tunables below matter. */}
        <Row
          label={isAdminProject ? 'Jobs, deployment-wide' : 'Jobs for this project'}
          hint={
            overrides[ENGINE_KEY] === false
              ? (isAdminProject
                ? 'switched OFF — every project following the default is paused'
                : 'switched OFF — this project’s schedules will not fire')
              : overrides[ENGINE_KEY] === true
                ? 'switched ON explicitly — unaffected by a deployment-wide pause'
                // The admin project has nothing above it to inherit from, so
                // "following the deployment default" would be circular there.
                : (isAdminProject
                  ? 'no explicit switch — the environment’s JOB_ENGINE_ENABLED applies'
                  : 'following the deployment default')
          }
          value={
            <Pill ok={overrides[ENGINE_KEY] !== false}>
              {overrides[ENGINE_KEY] === false ? 'paused' : 'running'}
            </Pill>
          }
          overridden={!isAdminProject && typeof overrides[ENGINE_KEY] === 'boolean'}
        />
        <Row
          label="Max concurrency"
          hint="ceiling on simultaneous runs"
          value={effective('maxConcurrency', d.maxConcurrency)}
          overridden={badge('maxConcurrency')}
        />
        <Row
          label="Claim lock TTL"
          hint="must exceed the longest job, or a slow run can be re-claimed while still executing"
          value={`${effective('lockTtlSeconds', d.lockTtlSeconds)}s`}
          overridden={badge('lockTtlSeconds')}
        />
        <Row
          label="Run retention"
          value={(() => {
            const v = effective('runRetentionDays', d.runRetentionDays);
            return v === 0 ? 'kept forever' : `${v} days`;
          })()}
          overridden={badge('runRetentionDays')}
        />
        <Row
          label="Next due"
          hint="soonest fire across every enabled schedule"
          value={untilNextRun(d.nextRunAt)}
        />
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <h3 className="mb-2 text-sm font-semibold">External heartbeat</h3>
        <Row
          label="Global tick token"
          hint="drives EVERY project's due work; set in the environment, never shown here"
          value={
            <Pill ok={d.globalTickConfigured}>
              {d.globalTickConfigured ? 'configured' : 'not set'}
            </Pill>
          }
        />
        {/* This project's own token, beside the global one. Without it the card
            described per-project tokens as something configured elsewhere while
            saying nothing about whether THIS project had one — the question an
            operator is actually on this screen to answer. Presence only: the value
            is write-only and never leaves the server. */}
        <Row
          label="This project's tick token"
          hint="lets an external scheduler drive only this project's jobs; set in App Configuration"
          value={
            <Pill ok={!!settingsQ.data?.hasTickToken}>
              {settingsQ.data?.hasTickToken ? 'configured' : 'not set'}
            </Pill>
          }
        />
        <Row label="Endpoint" value={<code className="text-xs">POST {d.tickPath}</code>} />

        <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs dark:border-blue-800 dark:bg-blue-900/20">
          <p className="mb-2">
            An external scheduler can drive cycles instead of, or alongside, the in-process
            poller. Both share the same atomic claim, so running both cannot double-fire a
            schedule.
          </p>
          <pre className="overflow-x-auto rounded bg-white p-2 dark:bg-gray-900">{`curl -X POST $BASE_URL${d.tickPath} \\
  -H "X-Job-Tick-Token: <global token>"`}</pre>
          <p className="mt-2">
            To drive only one project's jobs, give that project its own token under{' '}
            <strong>App Configuration → BackgroundJobs.TickToken</strong>, and send it with an{' '}
            <code>X-Job-Tick-Project</code> header. Jobs with no owning project are shared
            infrastructure and are driven by both.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
